import React from "react";
import { Option } from "./DropdownList";
import ConfirmModal from "components/ConfirmModal";
import { useTranslation } from "react-i18next";
import { propertyManagerStore } from "../PropertyManagerStore";
import { Property } from "features/BuildingProductEditor/ProductEditorStore";

const useSelect = (
  property: Property,
  handleRemoveOption: (item: Option | null) => void
) => {
  const { t } = useTranslation();
  const [options, setOptions] = React.useState<Option[]>([]);
  const [showRemoveConfirm, setShowRemoveConfirm] = React.useState<{
    state: boolean;
    option: Option | null;
  }>({
    state: false,
    option: null,
  });

  function sortAndSetOptions(optionsList: Option[] = []) {
    const sortedList = optionsList.sort((a, b) => a.name.localeCompare(b.name));
    setOptions(sortedList);
  }

  React.useEffect(() => {
    const optionsList = property.options?.map((rec) => ({
      id: rec,
      name: rec,
    }));
    sortAndSetOptions(optionsList);
  }, []);

  async function updateOptions(options: Option[], value: string[] = []) {
    sortAndSetOptions(options);
    await propertyManagerStore.updateProperty({
      ...property,
      value: value,
      options: options.map((rec) => rec.id),
    });
  }

  async function addOption(item: Option) {
    const updatedList = [...options, { id: item.name, name: item.name }];
    await updateOptions(updatedList);
  }

  const handleSearchTerm = (item: Option) => {
    if (options.find((rec) => rec.name === item.name)) {
      return;
    }
    addOption(item);
  };

  const onClickRemoveOption = async (item: Option) => {
    const checkIsUsed = await propertyManagerStore.checkPropertyValueIsInUse(
      property.slug,
      item.id
    );

    const onlyUsedInCurrectProduct =
      (property.value as string[])?.includes(item.name) &&
      checkIsUsed?.count === 1;

    if (!checkIsUsed?.count || onlyUsedInCurrectProduct) {
      handleRemoveOption(item);
    } else if (!showRemoveConfirm.state && checkIsUsed?.count) {
      setShowRemoveConfirm({ state: true, option: item });
    }
  };

  const removeConfirmPopup = () => {
    if (!showRemoveConfirm.state) return;
    return (
      <ConfirmModal
        type="warning"
        confirmButtonTitle={t("productEditor.removeOption")}
        open={showRemoveConfirm.state}
        close={() => setShowRemoveConfirm({ state: false, option: null })}
        title={t("productEditor.removeOptionTitle")}
        descriptionMesage={t("productEditor.removeOptionMsg", {
          option_name: showRemoveConfirm.option?.name,
        })}
        onConfirm={() => handleRemoveOption(showRemoveConfirm.option)}
        cancelTitle={t("commons.cancel")}
        confirmButtonType="error"
      />
    );
  };

  return {
    removeConfirmPopup,
    options,
    updateOptions,
    handleSearchTerm,
    onClickRemoveOption,
  };
};

export default useSelect;
